import React, { useCallback, useRef, useState, useEffect } from 'react';
import { DatePicker, TreeSelect, Switch, Space, Row, Col, Button, Dropdown, Divider, Tooltip, Spin, Checkbox } from 'antd';
import styles from './index.module.scss';
import {
	DownOutlined,
	QuestionCircleOutlined,
	DownloadOutlined,
	SettingOutlined,
} from '@ant-design/icons';
import SyncDateRangeDropdown from '../SyncDateRangeDropdown';
import { VISIBLE_COLUMN_OPTIONS } from '../Table';
import { getOptionLabel, checkPermission, toastText } from 'utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { updateExpenseGroupAction, getExpenseGroupsAction } from 'redux/action/expenseGroupAction';
import { getApiCSV } from 'redux/apis';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';
import ClearFilter from 'components/Global/ClearFilter';
const { RangePicker } = DatePicker;
const TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/New_York';

// SearchAndFilter component props interface
interface SearchAndFilterProps {
	dateRange: [any, any] | null;
	setDateRange: (range: [any, any] | null) => void;
	selectedAccounts: string[];
	setSelectedAccounts: (accounts: string[]) => void;
	selectedCustomers: string[];
	setSelectedCustomers: (customers: string[]) => void;
	selectedClasses: string[];
	setSelectedClasses: (classes: string[]) => void;
	selectedLocations: string[];
	setSelectedLocations: (locations: string[]) => void;
	selectedEntityTypes: string[];
	selectedLineBasedOn: string[];
	selectedVendors: string[];
	setSelectedEntityTypes: (entityTypes: string[]) => void;
	setSelectedLineBasedOn: (lineBasedOn: string[]) => void;
	setSelectedVendors: (vendors: string[]) => void;
	isSummaryView: boolean;
	setIsSummaryView: (view: boolean) => void | Promise<void>;
	accountOptions: any[];
	customerOptions: any[];
	classOptions: any[];
	locationOptions: any[];
	vendorOptions?: any[];
	onBatchEdit: () => void;
	onDownloadAttachments: () => void;
	lastSyncTime: Date;
	handleSync: () => void;
	loading: boolean;
	switchLoading: boolean;
	syncLoading: boolean;
	expenseGroupOptions: any[];
	selectedExpenseGroup: string;
	expenseGroupMenu: React.ReactElement;
	selectedCount: number;
	expenseGroupId?: string;
	visibleColumnKeys: string[];
	onVisibleColumnsChange: (keys: string[]) => void;
	onSaveVisibleColumnsAsDefault?: () => void | Promise<void>;
}


const ExpenseTransactionSearchAndFilter: React.FC<SearchAndFilterProps> = ({
	dateRange,
	setDateRange,
	selectedAccounts,
	setSelectedAccounts,
	selectedCustomers,
	setSelectedCustomers,
	selectedClasses,
	setSelectedClasses,
	selectedLocations,
	setSelectedLocations,
	selectedEntityTypes,
	selectedLineBasedOn,
	selectedVendors,
	setSelectedEntityTypes,
	setSelectedLineBasedOn,
	setSelectedVendors,
	isSummaryView,
	setIsSummaryView,
	accountOptions,
	customerOptions,
	classOptions,
	locationOptions,
	vendorOptions = [],
	onBatchEdit,
	onDownloadAttachments,
	lastSyncTime,
	handleSync,
	loading,
	switchLoading,
	syncLoading,
	expenseGroupOptions,
	selectedExpenseGroup,
	expenseGroupMenu,
	selectedCount,
	expenseGroupId,
	visibleColumnKeys,
	onVisibleColumnsChange,
	onSaveVisibleColumnsAsDefault,
}) => {
	// Local state for immediate UI updates
	const [localAccounts, setLocalAccounts] = useState<string[]>(selectedAccounts);
	const [localCustomers, setLocalCustomers] = useState<string[]>(selectedCustomers);
	const [localClasses, setLocalClasses] = useState<string[]>(selectedClasses);
	const [localLocations, setLocalLocations] = useState<string[]>(selectedLocations);
	const [localVendors, setLocalVendors] = useState<string[]>(selectedVendors);
	const [isUpdatingAccounts, setIsUpdatingAccounts] = useState(false);
	const [isUpdatingCustomers, setIsUpdatingCustomers] = useState(false);
	const [isUpdatingClasses, setIsUpdatingClasses] = useState(false);
	const [isUpdatingLocations, setIsUpdatingLocations] = useState(false);
	const [isUpdatingVendors, setIsUpdatingVendors] = useState(false);
	const [isExportLoading, setIsExportLoading] = useState(false);
	const [accountsTooltipDisabled, setAccountsTooltipDisabled] = useState(false);
	const [customersTooltipDisabled, setCustomersTooltipDisabled] = useState(false);
	const [classesTooltipDisabled, setClassesTooltipDisabled] = useState(false);
	const [locationsTooltipDisabled, setLocationsTooltipDisabled] =
		useState(false);
	const [vendorsTooltipDisabled, setVendorsTooltipDisabled] = useState(false);
	const [visibleColumnsDropdownOpen, setVisibleColumnsDropdownOpen] =
		useState(false);
	const [saveAsDefaultLoading, setSaveAsDefaultLoading] = useState(false);
	const visibleColumnsDropdownScrollRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();

	// Lock body scroll when Visible Columns dropdown is open so the app doesn't scroll behind it
	useEffect(() => {
		if (visibleColumnsDropdownOpen) {
			const prev = document.body.style.overflow;
			document.body.style.overflow = 'hidden';
			return () => {
				document.body.style.overflow = prev;
			};
		}
	}, [visibleColumnsDropdownOpen]);

	const handleVisibleColumnToggle = (key: string, checked: boolean) => {
		if (checked) {
			onVisibleColumnsChange([...visibleColumnKeys, key]);
		} else {
			// Require at least one visible column
			if (visibleColumnKeys.length <= 1) {
				toastText('At least one column must remain visible.', 'error');
				return;
			}
			onVisibleColumnsChange(visibleColumnKeys.filter((k) => k !== key));
		}
	};

	const handleSaveVisibleColumnsAsDefault = async () => {
		if (!onSaveVisibleColumnsAsDefault) return;
		setSaveAsDefaultLoading(true);
		try {
			await onSaveVisibleColumnsAsDefault();
			setVisibleColumnsDropdownOpen(false);
		} finally {
			setSaveAsDefaultLoading(false);
		}
	};

	// Redux
	const dispatch = useDispatch<AppDispatch>();
	const { isUpdateLoading: expenseGroupLoading } = useSelector((state: any) => state.expenseGroups);
	const selectedCompanyPermission = useSelector(
		(state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
	);
	const sortConfig = useSelector(
		(state: any) => state?.transactions?.sortConfig
	);
	const transactions = useSelector(
		(state: any) => state?.transactions?.transactions
	);
	const transactionsFirstTimeLoading = useSelector(
		(state: any) => state?.transactions?.fistTimeFetchLoading
	);

	// Summary view state
	const summaryData = useSelector((state: any) => state?.summary?.summary);
	const summaryLoading = useSelector((state: any) => state?.summary?.isLoading);
	const summaryFirstTimeLoading = useSelector(
		(state: any) => state?.summary?.fistTimeFetchLoading
	);

	// Permission checks
	const isEditPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Expense Transactions',
		permission: ['edit'],
	});

	const isAddPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Expense Transactions',
		permission: ['add'],
	});

	// Permission check for Expense Groups (used for Update buttons)
	const isEditExpenseGroupPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Expense Groups',
		permission: ['edit'],
	});

	// Sync Now button requires both add and edit permissions
	const isSyncPermission = isAddPermission && isEditPermission;

	// Debounce refs for parent state updates
	const debounceRefs = {
		accounts: useRef<NodeJS.Timeout | null>(null),
		customers: useRef<NodeJS.Timeout | null>(null),
		classes: useRef<NodeJS.Timeout | null>(null),
		locations: useRef<NodeJS.Timeout | null>(null),
		vendors: useRef<NodeJS.Timeout | null>(null),
	};

	// Add blank options to dropdown data
	const enhancedCustomerOptions = [
		{
			title: 'Blank Customer',
			value: '',
			key: 'blank',
			disabled: false,
			children: []
		},
		...customerOptions
	];

	const enhancedClassOptions = [
		{
			title: 'Blank Class',
			value: '',
			key: 'blank',
			disabled: false,
			children: []
		},
		...classOptions
	];

	const enhancedLocationOptions = [
		{
			title: 'Blank Location',
			value: '',
			key: 'blank',
			disabled: false,
			children: []
		},
		...locationOptions
	];


	// Sync local state with props when they change externally
	useEffect(() => {
		setLocalAccounts(selectedAccounts);
	}, [selectedAccounts]);

	useEffect(() => {
		// Transform 'blank' back to empty strings for display
		const transformedCustomers = selectedCustomers.map((customer: string) =>
			customer === 'blank' ? '' : customer
		);
		setLocalCustomers(transformedCustomers);
	}, [selectedCustomers]);

	useEffect(() => {
		// Transform 'blank' back to empty strings for display
		const transformedClasses = selectedClasses.map((classItem: string) =>
			classItem === 'blank' ? '' : classItem
		);
		setLocalClasses(transformedClasses);
	}, [selectedClasses]);

	useEffect(() => {
		// Transform 'blank' back to empty strings for display
		const transformedLocations = selectedLocations.map((location: string) =>
			location === 'blank' ? '' : location
		);
		setLocalLocations(transformedLocations);
	}, [selectedLocations]);

	useEffect(() => {
		setLocalVendors(selectedVendors);
	}, [selectedVendors]);

	// Debounced handlers for parent state updates
	const debouncedSetAccounts = useCallback((accounts: string[]) => {
		setLocalAccounts(accounts); // Update UI immediately
		if (debounceRefs.accounts.current) {
			clearTimeout(debounceRefs.accounts.current);
		}
		debounceRefs.accounts.current = setTimeout(() => {
			setSelectedAccounts(accounts);
		}, 500);
	}, [setSelectedAccounts]);


	const debouncedSetCustomers = useCallback((customers: string[]) => {
		setLocalCustomers(customers); // Update UI immediately
		if (debounceRefs.customers.current) {
			clearTimeout(debounceRefs.customers.current);
		}
		debounceRefs.customers.current = setTimeout(() => {
			// Transform empty strings to 'blank' for backend consistency
			const transformedCustomers = customers.map((customer: string) =>
				customer === '' ? 'blank' : customer
			);
			setSelectedCustomers(transformedCustomers);
		}, 500);
	}, [setSelectedCustomers]);


	const debouncedSetClasses = useCallback((classes: string[]) => {
		setLocalClasses(classes); // Update UI immediately
		if (debounceRefs.classes.current) {
			clearTimeout(debounceRefs.classes.current);
		}
		debounceRefs.classes.current = setTimeout(() => {
			// Transform empty strings to 'blank' for backend consistency
			const transformedClasses = classes.map((classItem: string) =>
				classItem === '' ? 'blank' : classItem
			);
			setSelectedClasses(transformedClasses);
		}, 500);
	}, [setSelectedClasses]);


	const debouncedSetLocations = useCallback((locations: string[]) => {
		setLocalLocations(locations); // Update UI immediately
		if (debounceRefs.locations.current) {
			clearTimeout(debounceRefs.locations.current);
		}
		debounceRefs.locations.current = setTimeout(() => {
			// Transform empty strings to 'blank' for backend consistency
			const transformedLocations = locations.map((location: string) =>
				location === '' ? 'blank' : location
			);
			setSelectedLocations(transformedLocations);
		}, 500);
	}, [setSelectedLocations]);

	const debouncedSetVendors = useCallback((vendors: string[]) => {
		setLocalVendors(vendors);
		if (debounceRefs.vendors.current) {
			clearTimeout(debounceRefs.vendors.current);
		}
		debounceRefs.vendors.current = setTimeout(() => {
			setSelectedVendors(vendors);
		}, 500);
	}, [setSelectedVendors]);

	// Handle updating accounts for the expense group
	const handleUpdateAccounts = async () => {
		if (!expenseGroupId) {
			console.error('No expense group ID provided');
			return;
		}

		setIsUpdatingAccounts(true);
		try {
			// Update only the accounts field
			await dispatch(updateExpenseGroupAction({
				id: expenseGroupId,
				accounts: localAccounts,
			})).unwrap();
			// Refetch all expense groups to get latest values after update
			await dispatch(getExpenseGroupsAction({ page: 1, limit: 150 }));
		} catch (error) {
			console.error('Failed to update accounts:', error);
		} finally {
			setIsUpdatingAccounts(false);
		}
	};

	// Handle updating customers for the expense group
	const handleUpdateCustomers = async () => {
		if (!expenseGroupId) {
			console.error('No expense group ID provided');
			return;
		}

		setIsUpdatingCustomers(true);
		try {
			// Transform empty strings to 'blank' for backend consistency
			const transformedCustomers = localCustomers.map((customer: string) =>
				customer === '' ? 'blank' : customer
			);

			// Update only the customers field
			await dispatch(updateExpenseGroupAction({
				id: expenseGroupId,
				customers: transformedCustomers,
			})).unwrap();
			// Refetch all expense groups to get latest values after update
			await dispatch(getExpenseGroupsAction({ page: 1, limit: 150 }));
		} catch (error) {
			console.error('Failed to update customers:', error);
		} finally {
			setIsUpdatingCustomers(false);
		}
	};

	// Handle updating classes for the expense group
	const handleUpdateClasses = async () => {
		if (!expenseGroupId) {
			console.error('No expense group ID provided');
			return;
		}

		setIsUpdatingClasses(true);
		try {
			// Transform empty strings to 'blank' for backend consistency
			const transformedClasses = localClasses.map((classItem: string) =>
				classItem === '' ? 'blank' : classItem
			);

			// Update only the classes field
			await dispatch(updateExpenseGroupAction({
				id: expenseGroupId,
				classes: transformedClasses,
			})).unwrap();
			// Refetch all expense groups to get latest values after update
			await dispatch(getExpenseGroupsAction({ page: 1, limit: 150 }));
		} catch (error) {
			console.error('Failed to update classes:', error);
		} finally {
			setIsUpdatingClasses(false);
		}
	};

	// Handle updating locations for the expense group
	const handleUpdateLocations = async () => {
		if (!expenseGroupId) {
			console.error('No expense group ID provided');
			return;
		}

		setIsUpdatingLocations(true);
		try {
			// Transform empty strings to 'blank' for backend consistency
			const transformedLocations = localLocations.map((location: string) =>
				location === '' ? 'blank' : location
			);

			// Update only the locations field
			await dispatch(updateExpenseGroupAction({
				id: expenseGroupId,
				locations: transformedLocations,
			})).unwrap();
			// Refetch all expense groups to get latest values after update
			await dispatch(getExpenseGroupsAction({ page: 1, limit: 150 }));
		} catch (error) {
			console.error('Failed to update locations:', error);
		} finally {
			setIsUpdatingLocations(false);
		}
	};

	// Handle updating vendors for the expense group
	const handleUpdateVendors = async () => {
		if (!expenseGroupId) {
			console.error('No expense group ID provided');
			return;
		}

		setIsUpdatingVendors(true);
		try {
			const transformedVendors = localVendors.map((vendor: string) =>
				vendor === '' ? 'blank' : vendor
			);

			await dispatch(updateExpenseGroupAction({
				id: expenseGroupId,
				vendors: transformedVendors,
			})).unwrap();
			await dispatch(getExpenseGroupsAction({ page: 1, limit: 150 }));
		} catch (error) {
			console.error('Failed to update vendors:', error);
		} finally {
			setIsUpdatingVendors(false);
		}
	};

	// Handle clear all filters
	const handleClearFilters = useCallback(() => {
		// Check if there are any active filters
		const hasActiveFilters =
			(selectedAccounts && selectedAccounts.length > 0) ||
			(selectedCustomers && selectedCustomers.length > 0) ||
			(selectedClasses && selectedClasses.length > 0) ||
			(selectedLocations && selectedLocations.length > 0) ||
			(selectedEntityTypes && selectedEntityTypes.length > 0) ||
			(selectedLineBasedOn && selectedLineBasedOn.length > 0) ||
			(selectedVendors && selectedVendors.length > 0);

		// If no filters are active, do nothing
		if (!hasActiveFilters) {
			return;
		}

		// Clear all debounce timers
		if (debounceRefs.accounts.current) {
			clearTimeout(debounceRefs.accounts.current);
		}
		if (debounceRefs.customers.current) {
			clearTimeout(debounceRefs.customers.current);
		}
		if (debounceRefs.classes.current) {
			clearTimeout(debounceRefs.classes.current);
		}
		if (debounceRefs.locations.current) {
			clearTimeout(debounceRefs.locations.current);
		}
		if (debounceRefs.vendors.current) {
			clearTimeout(debounceRefs.vendors.current);
		}

		// Clear local state immediately
		setLocalAccounts([]);
		setLocalCustomers([]);
		setLocalClasses([]);
		setLocalLocations([]);
		setLocalVendors([]);

		// Clear parent state
		setSelectedAccounts([]);
		setSelectedCustomers([]);
		setSelectedClasses([]);
		setSelectedLocations([]);
		setSelectedEntityTypes([]);
		setSelectedLineBasedOn([]);
		setSelectedVendors([]);
	}, [
		selectedAccounts,
		selectedCustomers,
		selectedClasses,
		selectedLocations,
		selectedEntityTypes,
		selectedLineBasedOn,
		selectedVendors,
		setSelectedAccounts,
		setSelectedCustomers,
		setSelectedClasses,
		setSelectedLocations,
		setSelectedEntityTypes,
		setSelectedLineBasedOn,
		setSelectedVendors,
	]);

	// Handle export transactions
	const handleExportTransactions = async () => {
		if (!dateRange || !dateRange[0] || !dateRange[1]) {
			toastText('Please select a date range', 'error');
			return;
		}

		try {
			setIsExportLoading(true);
			const params: any = {
				companyId: localStorage.getItem('companyId'),
				fromDate: dateRange[0].format('YYYY-MM-DD'),
				toDate: dateRange[1].format('YYYY-MM-DD'),
			};

			// Only include filter parameters if they have values
			if (selectedAccounts.length > 0) {
				params.accountIds = selectedAccounts;
			}
			if (selectedCustomers.length > 0) {
				params.customerIds = selectedCustomers;
			}
			if (selectedClasses.length > 0) {
				params.classIds = selectedClasses;
			}
			if (selectedLocations.length > 0) {
				params.locationIds = selectedLocations;
			}

			if (selectedVendors.length > 0) {
				params.vendorIds = selectedVendors;
			}
			// Include sort parameters from Redux state
			if (isSummaryView) {
				const allowedSummarySortFields = [
					'accountName',
					'customerName',
					'className',
					'locationName',
					'vendorName',
					'totalAmount',
				];
				const desiredSortBy = sortConfig?.sortBy;
				if (desiredSortBy && allowedSummarySortFields.includes(desiredSortBy)) {
					params.sortBy = desiredSortBy;
				} else {
					params.sortBy = 'totalAmount';
				}
				params.sortOrder = sortConfig?.sortOrder || 'desc';
			} else {
				if (sortConfig?.sortBy) {
					params.sortBy = sortConfig.sortBy;
				}
				if (sortConfig?.sortOrder) {
					params.sortOrder = sortConfig.sortOrder;
				}
				if (selectedEntityTypes.length > 0) {
					params.entityTypes = selectedEntityTypes;
				}
				if (selectedLineBasedOn.length > 0) {
					params.lineBasedOn = selectedLineBasedOn;
				}
				if (selectedVendors.length > 0) {
					params.vendorIds = selectedVendors;
				}
			}

			// Choose endpoint based on current view (detailed vs summary)
			const exportEndpoint = isSummaryView
				? '/transactions/summary/export-csv'
				: '/transactions/export-csv';

			const response: any = await getApiCSV(exportEndpoint, params);

			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'text/csv',
				})
			);

			const fileName = moment.tz(new Date(), TIMEZONE).format('MMDDYYYYhhmmss');
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `Transactions_${fileName}.csv`);
			document.body.appendChild(link);
			link.click();
			link.remove();

			setIsExportLoading(false);
		} catch (err: any) {
			if (err?.response?.data?.error?.status == 401) {
				navigate('/login');
				window.location.reload();
			}
			// Handle nested error structure - prefer additionalInfo message if available
			const errorData = err?.response?.data?.error;
			let errorMessage = 'Error while exporting transactions CSV';
			if (errorData?.additionalInfo?.message) {
				errorMessage = errorData.additionalInfo.message;
			} else if (errorData?.message) {
				errorMessage = errorData.message;
			} else if (err?.response?.data?.message) {
				errorMessage = err.response.data.message;
			}
			toastText(errorMessage, 'error');
			console.error(err);
			setIsExportLoading(false);
		}
	};

	return (
		<div className="search-filter">
			<div className="search-filter__wrapper">
				<Row justify="space-between" align="middle">
					<Col span={12}>
						<Space wrap>
							<Dropdown overlay={expenseGroupMenu} trigger={['click']}>
								<Button
									className="expense-group-dropdown-button"
									title={
										expenseGroupOptions.find(
											(opt) => opt.value === selectedExpenseGroup
										)?.label
									}
								>
									<span className="expense-group-button-text">
										{
											expenseGroupOptions.find(
												(opt) => opt.value === selectedExpenseGroup
											)?.label
										}
									</span>
									<DownOutlined />
								</Button>
							</Dropdown>
							<RangePicker
								placeholder={['Start Date', 'End Date']}
								value={dateRange}
								onChange={(dates) => {
									// only call setDateRange if it's not empty
									if (dates && dates.length === 2) {
										setDateRange(dates);
									}
								}}
								style={{ minWidth: 250, height: 40, fontSize: '12px' }}
								size="large"
								allowClear={false}
								format="MM/DD/YYYY"
							/>
						</Space>
					</Col>
					<Col span={12} style={{ textAlign: 'right' }}>
						<Space>
							{/* Hide export button when loading or when there's no transaction data */}

							{isSyncPermission && (
								<>
									<SyncDateRangeDropdown
										onSync={handleSync}
										isLoading={syncLoading}
										lastSyncTime={lastSyncTime}
										defaultDateRange={dateRange}
									/>
									<Divider type="vertical" className={styles['divider']} />
								</>
							)}

							<span
								style={{ fontSize: '12px', color: '#344735', fontWeight: 600 }}
							>
								Detailed
							</span>
							<Tooltip title="Toggle between detailed transaction view and summarized transaction view">
								<span>
									<Spin spinning={switchLoading} size="small">
										<Switch
											checked={isSummaryView}
											onChange={setIsSummaryView}
											disabled={switchLoading}
										/>
									</Spin>
								</span>
							</Tooltip>
							<span
								style={{ fontSize: '12px', color: '#344735', fontWeight: 600 }}
							>
								Summarized
							</span>
							<Divider type="vertical" className={styles['divider']} />
							<Dropdown
								open={visibleColumnsDropdownOpen}
								onOpenChange={setVisibleColumnsDropdownOpen}
								trigger={['click']}
								placement="bottomLeft"
								dropdownRender={() => (
									<div
										style={{
											background: '#fff',
											borderRadius: '8px',
											boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
											padding: '12px 16px',
											minWidth: 220,
										}}
										onClick={(e) => e.stopPropagation()}
										onWheel={(e) => {
											e.stopPropagation();
											// Only prevent document/table scroll when wheel is outside the scrollable list
											if (!visibleColumnsDropdownScrollRef.current?.contains(e.target as Node)) {
												e.preventDefault();
											}
										}}
									>
										<div
											style={{
												fontWeight: 600,
												marginBottom: 12,
												fontSize: 12,
											}}
										>
											Visible Columns
										</div>
										<div
											ref={visibleColumnsDropdownScrollRef}
											style={{
												display: 'flex',
												flexDirection: 'column',
												gap: 8,
												maxHeight: 280,
												overflowY: 'auto',
												overflowX: 'hidden',
											}}
										>
											{VISIBLE_COLUMN_OPTIONS.map((opt) => {
												const isChecked = visibleColumnKeys.includes(opt.key);
												const isLastVisibleColumn =
													visibleColumnKeys.length === 1 && isChecked;
												return (
													<Tooltip
														key={opt.key}
														title={
															isLastVisibleColumn
																? 'At least one column must remain visible'
																: undefined
														}
													>
														<div>
															<Checkbox
																checked={isChecked}
																disabled={isLastVisibleColumn}
																onChange={(e) =>
																	handleVisibleColumnToggle(
																		opt.key,
																		e.target.checked,
																	)
																}
															>
																<span style={{ fontSize: '12px' }}>
																	{opt.label}
																</span>
															</Checkbox>
														</div>
													</Tooltip>
												);
											})}
										</div>
										<Button
											type="primary"
											block
											loading={saveAsDefaultLoading}
											style={{
												marginTop: 12,
												backgroundColor: '#344734',
												borderColor: '#344734',
												fontSize: 12,
											}}
											onClick={handleSaveVisibleColumnsAsDefault}
										>
											Save as Default
										</Button>
									</div>
								)}
							>
								<Tooltip title="Choose visible columns">
									<Button
										style={{
											height: '40px',
											width: 40,
											// padding: 0,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											backgroundColor: '#344735',
											borderColor: '#344735',
											borderRadius: '12px',
											color: '#fff',
										}}
									>
										<SettingOutlined />
									</Button>
								</Tooltip>
							</Dropdown>
							{(() => {
								return (
									<Divider type="vertical" className={styles['divider']} />
								);
							})()}

							{/* Disable export button when loading or when there's no transaction data */}
							{(() => {
								const detailedReady =
									!transactionsFirstTimeLoading &&
									!loading &&
									transactions &&
									transactions.length > 0;
								const summaryReady =
									!summaryFirstTimeLoading &&
									!summaryLoading &&
									summaryData &&
									summaryData.length > 0;
								const canExport =
									!switchLoading &&
									(isSummaryView ? summaryReady : detailedReady);
								const isDisabled = isExportLoading || !canExport;
								return (
									<Button
										onClick={handleExportTransactions}
										disabled={isDisabled}
										// loading={isExportLoading}
										style={{
											height: '40px',
											fontSize: '12px',
											display: 'flex',
											alignItems: 'center',
											gap: '1rem',
											border: `1px solid ${isDisabled ? '#d9d9d9' : '#344734'}`,
											color: isDisabled ? 'rgba(0, 0, 0, 0.25)' : '#344734',
											backgroundColor: isDisabled ? '#f5f5f5' : '#ffffff',
											borderRadius: '12px',
											fontWeight: '600',
											padding: '1.2rem 2.8rem',
											cursor: isDisabled ? 'not-allowed' : 'pointer',
											transition: 'all 0.2s ease-in-out',
										}}
									>
										Export
										<span>
											{isExportLoading ? (
												<Spin
													size="small"
													className={styles['export-spinner']}
												/>
											) : (
												<DownloadOutlined />
											)}
										</span>
									</Button>
								);
							})()}
						</Space>
					</Col>
				</Row>

				{/* <Divider style={{ margin: '12px 0', borderColor: '#344735' }} /> */}

				<Row
					justify="space-between"
					align="middle"
					gutter={[16, 16]}
					wrap
					className={styles['second-row']}
				>
					<Col flex="auto">
						<Space wrap>
							<TreeSelect
								placeholder="Accounts"
								multiple
								allowClear
								showSearch
								value={localAccounts}
								onChange={debouncedSetAccounts}
								treeData={accountOptions}
								style={{ minWidth: 200, height: 40, fontSize: '12px' }}
								dropdownStyle={{ minWidth: 350 }}
								size="large"
								treeLine
								maxTagCount={0}
								treeDefaultExpandAll
								filterTreeNode={(inputValue, treeNode) =>
									treeNode?.props?.title
										?.toLowerCase()
										.includes(inputValue.toLowerCase())
								}
								onDropdownVisibleChange={(open) => {
									setAccountsTooltipDisabled(open);
								}}
								dropdownRender={(menu) => (
									<>
										{menu}
										{expenseGroupId &&
											expenseGroupId !== 'all' &&
											accountOptions.length > 0 &&
											isEditExpenseGroupPermission && (
												<>
													<Divider style={{ margin: '8px 0' }} />
													<div style={{ padding: '0 8px 4px', display: 'flex', justifyContent: 'flex-start' }}>
														<Button
															className="btn-black"
															loading={
																isUpdatingAccounts || expenseGroupLoading
															}
															onClick={handleUpdateAccounts}
															disabled={
																isUpdatingAccounts || expenseGroupLoading
															}
															style={{
																fontSize: '12px',
																width: 'auto',
																minWidth: '120px',
																textAlign: 'left',
															}}
														>
															<span>Update</span>
															<QuestionCircleOutlined
																style={{ fontSize: '12px' }}
																onClick={(e) => e.stopPropagation()}
																title="This will update accounts for selected expense group"
															/>
														</Button>
													</div>
												</>
											)}
									</>
								)}
								maxTagPlaceholder={() => {
									if (localAccounts.length === 0) return 'Accounts';
									const labels = localAccounts.map((v) =>
										getOptionLabel(v, accountOptions)
									);
									return (
										<Tooltip
											title={
												<div className="filter-tooltip-content">
													{labels.map((l, i) => (
														<div
															key={i}
															className="filter-tooltip-item"
															style={{
																borderBottom:
																	i !== labels.length - 1
																		? '1px solid rgba(52, 71, 53, 0.1)'
																		: 'none',
															}}
														>
															{l}
														</div>
													))}
												</div>
											}
											placement="bottom"
											overlayClassName="filter-tooltip"
											visible={accountsTooltipDisabled ? false : undefined}
											trigger={accountsTooltipDisabled ? [] : ['hover']}
										>
											Accounts ({localAccounts.length})
										</Tooltip>
									);
								}}
							/>
							<TreeSelect
								placeholder="Customers"
								multiple
								allowClear
								showSearch
								value={localCustomers}
								onChange={debouncedSetCustomers}
								treeData={enhancedCustomerOptions}
								style={{ minWidth: 200, height: 40, fontSize: '12px' }}
								dropdownStyle={{ minWidth: 350 }}
								treeLine
								maxTagCount={0}
								treeDefaultExpandAll
								filterTreeNode={(inputValue, treeNode) =>
									treeNode?.props?.title
										?.toLowerCase()
										.includes(inputValue.toLowerCase())
								}
								onDropdownVisibleChange={(open) => {
									setCustomersTooltipDisabled(open);
								}}
								dropdownRender={(menu) => (
									<>
										{menu}
										{expenseGroupId &&
											expenseGroupId !== 'all' &&
											enhancedCustomerOptions.length > 0 &&
											isEditExpenseGroupPermission && (
												<>
													<Divider style={{ margin: '8px 0' }} />
													<div style={{ padding: '0 8px 4px', display: 'flex', justifyContent: 'flex-start' }}>
														<Button
															className="btn-black"
															loading={
																isUpdatingCustomers || expenseGroupLoading
															}
															onClick={handleUpdateCustomers}
															disabled={
																isUpdatingCustomers || expenseGroupLoading
															}
															style={{
																fontSize: '12px',
																width: 'auto',
																minWidth: '120px',
																textAlign: 'left',
															}}
														>
															<span>Update</span>
															<QuestionCircleOutlined
																style={{ fontSize: '12px' }}
																onClick={(e) => e.stopPropagation()}
																title="This will update customers for selected expense group"
															/>
														</Button>
													</div>
												</>
											)}
									</>
								)}
								maxTagPlaceholder={() => {
									if (localCustomers.length === 0) return 'Customers';
									const labels = localCustomers.map((v) =>
										v === '' ? 'Blank Customer' : getOptionLabel(v, enhancedCustomerOptions)
									);
									return (
										<Tooltip
											title={
												<div className="filter-tooltip-content">
													{labels.map((l, i) => (
														<div
															key={i}
															className="filter-tooltip-item"
															style={{
																borderBottom:
																	i !== labels.length - 1
																		? '1px solid rgba(52, 71, 53, 0.1)'
																		: 'none',
															}}
														>
															{l}
														</div>
													))}
												</div>
											}
											placement="bottom"
											overlayClassName="filter-tooltip"
											visible={customersTooltipDisabled ? false : undefined}
											trigger={customersTooltipDisabled ? [] : ['hover']}
										>
											Customers ({localCustomers.length})
										</Tooltip>
									);
								}}
								size="large"
							/>
							<TreeSelect
								placeholder="Classes"
								multiple
								allowClear
								showSearch
								value={localClasses}
								onChange={debouncedSetClasses}
								treeData={enhancedClassOptions}
								style={{ minWidth: 200, height: 40, fontSize: '12px' }}
								dropdownStyle={{ minWidth: 350 }}
								treeLine
								maxTagCount={0}
								treeDefaultExpandAll
								filterTreeNode={(inputValue, treeNode) =>
									treeNode?.props?.title
										?.toLowerCase()
										.includes(inputValue.toLowerCase())
								}
								onDropdownVisibleChange={(open) => {
									setClassesTooltipDisabled(open);
								}}
								dropdownRender={(menu) => (
									<>
										{menu}
										{expenseGroupId &&
											expenseGroupId !== 'all' &&
											enhancedClassOptions.length > 0 &&
											isEditExpenseGroupPermission && (
												<>
													<Divider style={{ margin: '8px 0' }} />
													<div style={{ padding: '0 8px 4px', display: 'flex', justifyContent: 'flex-start' }}>
														<Button
															className="btn-black"
															loading={
																isUpdatingClasses || expenseGroupLoading
															}
															onClick={handleUpdateClasses}
															disabled={
																isUpdatingClasses || expenseGroupLoading
															}
															style={{
																fontSize: '12px',
																width: 'auto',
																minWidth: '120px',
																textAlign: 'left',
															}}
														>
															<span>Update</span>
															<QuestionCircleOutlined
																style={{ fontSize: '12px' }}
																onClick={(e) => e.stopPropagation()}
																title="This will update classes for selected expense group"
															/>
														</Button>
													</div>
												</>
											)}
									</>
								)}
								maxTagPlaceholder={() => {
									if (localClasses.length === 0) return 'Classes';
									const labels = localClasses.map((v) =>
										v === '' ? 'Blank Class' : getOptionLabel(v, enhancedClassOptions)
									);
									return (
										<Tooltip
											title={
												<div className="filter-tooltip-content">
													{labels.map((l, i) => (
														<div
															key={i}
															className="filter-tooltip-item"
															style={{
																borderBottom:
																	i !== labels.length - 1
																		? '1px solid rgba(52, 71, 53, 0.1)'
																		: 'none',
															}}
														>
															{l}
														</div>
													))}
												</div>
											}
											placement="bottom"
											overlayClassName="filter-tooltip"
											visible={classesTooltipDisabled ? false : undefined}
											trigger={classesTooltipDisabled ? [] : ['hover']}
										>
											Classes ({localClasses.length})
										</Tooltip>
									);
								}}
								size="large"
							/>
							<TreeSelect
								placeholder="Locations"
								multiple
								allowClear
								showSearch
								value={localLocations}
								onChange={debouncedSetLocations}
								treeData={enhancedLocationOptions}
								style={{ minWidth: 200, height: 40, fontSize: '12px' }}
								dropdownStyle={{ minWidth: 350 }}
								treeLine
								maxTagCount={0}
								treeDefaultExpandAll
								filterTreeNode={(inputValue, treeNode) =>
									treeNode?.props?.title
										?.toLowerCase()
										.includes(inputValue.toLowerCase())
								}
								onDropdownVisibleChange={(open) => {
									setLocationsTooltipDisabled(open);
								}}
								dropdownRender={(menu) => (
									<>
										{menu}
										{expenseGroupId &&
											expenseGroupId !== 'all' &&
											enhancedLocationOptions.length > 0 &&
											isEditExpenseGroupPermission && (
												<>
													<Divider style={{ margin: '8px 0' }} />
													<div style={{ padding: '0 8px 4px', display: 'flex', justifyContent: 'flex-start' }}>
														<Button
															className="btn-black"
															loading={
																isUpdatingLocations || expenseGroupLoading
															}
															onClick={handleUpdateLocations}
															disabled={
																isUpdatingLocations || expenseGroupLoading
															}
															style={{
																fontSize: '12px',
																width: 'auto',
																minWidth: '120px',
																textAlign: 'left',
															}}
														>
															<span>Update</span>
															<QuestionCircleOutlined
																style={{ fontSize: '12px' }}
																onClick={(e) => e.stopPropagation()}
																title="This will update locations for selected expense group"
															/>
														</Button>
													</div>
												</>
											)}
									</>
								)}
								maxTagPlaceholder={() => {
									if (localLocations.length === 0) return 'Locations';
									const labels = localLocations.map((v) =>
										v === '' ? 'Blank Location' : getOptionLabel(v, enhancedLocationOptions)
									);
									return (
										<Tooltip
											title={
												<div className="filter-tooltip-content">
													{labels.map((l, i) => (
														<div
															key={i}
															className="filter-tooltip-item"
															style={{
																borderBottom:
																	i !== labels.length - 1
																		? '1px solid rgba(52, 71, 53, 0.1)'
																		: 'none',
															}}
														>
															{l}
														</div>
													))}
												</div>
											}
											placement="bottom"
											overlayClassName="filter-tooltip"
											visible={locationsTooltipDisabled ? false : undefined}
											trigger={locationsTooltipDisabled ? [] : ['hover']}
										>
											Locations ({localLocations.length})
										</Tooltip>
									);
								}}
								size="large"
							/>
							<TreeSelect
								placeholder="Vendors"
								multiple
								allowClear
								showSearch
								value={localVendors}
								onChange={debouncedSetVendors}
								treeData={vendorOptions || []}
								style={{ minWidth: 200, height: 40, fontSize: '12px' }}
								dropdownStyle={{ minWidth: 350 }}
								treeLine
								maxTagCount={0}
								treeDefaultExpandAll
								filterTreeNode={(inputValue, treeNode) =>
									treeNode?.props?.title
										?.toLowerCase()
										.includes(inputValue.toLowerCase())
								}
								onDropdownVisibleChange={(open) => setVendorsTooltipDisabled(open)}
								dropdownRender={(menu) => (
									<>
										{menu}
										{expenseGroupId &&
											expenseGroupId !== 'all' &&
											(vendorOptions?.length ?? 0) > 0 &&
											isEditExpenseGroupPermission && (
												<>
													<Divider style={{ margin: '8px 0' }} />
													<div style={{ padding: '0 8px 4px', display: 'flex', justifyContent: 'flex-start' }}>
														<Button
															className="btn-black"
															loading={
																isUpdatingVendors || expenseGroupLoading
															}
															onClick={handleUpdateVendors}
															disabled={
																isUpdatingVendors || expenseGroupLoading
															}
															style={{
																fontSize: '12px',
																width: 'auto',
																minWidth: '120px',
																textAlign: 'left',
															}}
														>
															<span>Update</span>
															<QuestionCircleOutlined
																style={{ fontSize: '12px' }}
																onClick={(e) => e.stopPropagation()}
																title="This will update vendors for selected expense group"
															/>
														</Button>
													</div>
												</>
											)}
									</>
								)}
								maxTagPlaceholder={() => {
									if (localVendors.length === 0) return 'Vendors';
									const labels = localVendors.map((v) =>
										getOptionLabel(v, vendorOptions || [])
									);
									return (
										<Tooltip
											title={
												<div className="filter-tooltip-content">
													{labels.map((l, i) => (
														<div
															key={i}
															className="filter-tooltip-item"
															style={{
																borderBottom:
																	i !== labels.length - 1
																		? '1px solid rgba(52, 71, 53, 0.1)'
																		: 'none',
															}}
														>
															{l}
														</div>
													))}
												</div>
											}
											placement="bottom"
											overlayClassName="filter-tooltip"
											visible={vendorsTooltipDisabled ? false : undefined}
											trigger={vendorsTooltipDisabled ? [] : ['hover']}
										>
											Vendors ({localVendors.length})
										</Tooltip>
									);
								}}
								size="large"
							/>
							<div className={styles['scaled-clear-filter']}>
								<ClearFilter clearFilter={handleClearFilters} />
							</div>
						</Space>
					</Col>

					<Col flex="none">
						<Space>
							{selectedCount > 0 && (
								<>
									{isEditPermission && (
										<Button
											className={styles['batch-edit']}
											onClick={onBatchEdit}
										>
											Batch Edit
										</Button>
									)}
									<Button
										className={`${styles['download-attachments']} ${styles['margin-top']}`}
										onClick={onDownloadAttachments}
									>
										Download Attachments
									</Button>
								</>
							)}
						</Space>
					</Col>
				</Row>
			</div>
		</div>
	);
};

export default ExpenseTransactionSearchAndFilter;
